import { Settings, ProductInfo, RangeSettings } from 'sofa-reborn-react';

import { ProductUtils } from '../../../util/ProductUtils';
import { localeService } from '../../../services';
import localReplacements from '../../../data/LocalReplacements.json';

import { ProductData, Product, SprItem } from '@ikea-aoa/component-view-3d';
import { ProductCache } from '@inter-ikea-kompis/services';
import { IProductItem, IProduct } from '@inter-ikea-kompis/types';

import DataHandler from '../utils/DataHandler';

export default class ProductInfoParser {
	public async parse(
		products: ProductData,
		rangeSettings: RangeSettings,
		locale: string,
		fakeData: IProduct[] | null,
		useLocalIds: boolean,
		fetchedProducts: IProduct[] | null,
		settings: Settings
	): Promise<ProductInfo> {
		const ikeaProducts: IProduct[] = await this.getIkeaProducts(
			fakeData,
			products,
			rangeSettings,
			fetchedProducts,
			settings,
			locale
		);
		const countryCode = localeService.getCountryCode();

		return this.getProductInfo(rangeSettings, countryCode, ikeaProducts, useLocalIds);
	}

	private getProductInfo(
		rangeSettings: RangeSettings,
		countryCode: string,
		ikeaProducts: IProduct[],
		useLocalIds: boolean
	) {
		const localSofabedMattressId = rangeSettings.localSofabedMattressId
			? rangeSettings.localSofabedMattressId[countryCode]
			: null;
		const newLocalSofabedMattressId = rangeSettings.newLocalSofabedMattressId
			? rangeSettings.newLocalSofabedMattressId[countryCode]
			: null;
		const globalSofabedMattressId = rangeSettings.globalSofabedMattressId
			? (rangeSettings.globalSofabedMattressId as string[])
			: [];
		const sofabedMattressId = rangeSettings.sofabedMattressId as string;
		const localIds = rangeSettings.localIds ? rangeSettings.localIds[countryCode] : null;
		const productInfo = new Object() as ProductInfo;

		ikeaProducts.forEach((i) => {
			const content = i.content;
			if (content) {
				productInfo[
					ProductUtils.getIkeaProductId(
						content,
						localSofabedMattressId,
						newLocalSofabedMattressId,
						globalSofabedMattressId,
						sofabedMattressId,
						useLocalIds,
						localIds
					)
				] = i;
			}
		});

		return productInfo;
	}

	private async getIkeaProducts(
		fakeData: IProduct[] | null,
		products: ProductData,
		rangeSettings: RangeSettings,
		fetchedProducts: IProduct[] | null,
		settings: Settings,
		locale: string
	) {
		const dataHandler = new DataHandler();
		let ikeaProducts: IProduct[] = [];
		if (fakeData) {
			ikeaProducts = fakeData;
		} else {
			const productItems = this.getProductItems(products, rangeSettings);
			if (fetchedProducts) {
				await this.useFetchProduct(
					productItems,
					fetchedProducts,
					ikeaProducts,
					dataHandler,
					settings
				);
			} else {
				ikeaProducts = await dataHandler.getProductFromService(settings, productItems.productItems);
			}
		}
		if (ikeaProducts && fakeData) {
			const cache = new ProductCache(locale);
			cache.addProducts(fakeData, dataHandler.sofaFields);
		}
		if (!ikeaProducts) {
			throw Error('Not able to fetch product data');
		}
		return ikeaProducts;
	}

	private async useFetchProduct(
		productItems: {
			productItems: IProductItem[];
			replacedItems: IProductItem[];
			groupedYmalProducts: IProductItem[];
		},
		fetchedProducts: IProduct[],
		ikeaProducts: IProduct[],
		dataHandler: DataHandler,
		settings: Settings
	) {
		const productIds = productItems.productItems.map((item) => `${item.type}-${item.id}`);
		const ymalIds = productItems.groupedYmalProducts.map(
			(item) => `${item.type.toUpperCase()}-${item.id}`
		);

		fetchedProducts.forEach((product) => {
			if (
				ymalIds.includes(product.itemId) ||
				productIds.includes(product.itemId) ||
				productIds.includes(`${product.content?.itemType}-${product.content?.itemNoLocal}`)
			) {
				ikeaProducts.push(product);
			}
		});
		if (productItems.replacedItems.length > 0) {
			const serviceProducts = await dataHandler.getProductFromService(
				settings,
				productItems.replacedItems
			);
			ikeaProducts.push(...serviceProducts);
		}
	}

	private getProductItems(
		products: ProductData,
		rangeSettings: RangeSettings
	): {
		productItems: IProductItem[];
		replacedItems: IProductItem[];
		groupedYmalProducts: IProductItem[];
	} {
		const countryCode = localeService.getCountryCode();
		const localSofabedMattressId = rangeSettings.localSofabedMattressId
			? rangeSettings.localSofabedMattressId[countryCode]
			: null;
		const newLocalSofabedMattressId = rangeSettings.newLocalSofabedMattressId
			? rangeSettings.newLocalSofabedMattressId[countryCode]
			: null;
		const globalSofabedMattressId = rangeSettings.globalSofabedMattressId
			? (rangeSettings.globalSofabedMattressId as string[])
			: [];
		const productItems = new Array<IProductItem>();
		const replacedItems = new Array<IProductItem>();
		const exists: string[] = [];
		const ymalProducts = rangeSettings.ymalProducts ? rangeSettings.ymalProducts : {};
		const groupedYmalProducts = new Array<IProductItem>();

		Object.values(ymalProducts).forEach((cover) => {
			cover.forEach((c) => {
				groupedYmalProducts.push(c);
			});
		});

		Object.values(products).forEach((product) => {
			const { productId, type, sprItems } = product as Product;
			productItems.push({ id: productId, type });
			if (sprItems && sprItems.length) {
				sprItems.forEach((item) => {
					const itemId = this.getLocalReplacement(
						localSofabedMattressId === globalSofabedMattressId[0]
							? localSofabedMattressId
							: newLocalSofabedMattressId,
						item,
						globalSofabedMattressId,
						countryCode
					);
					if (!exists.includes(itemId)) {
						if (item.itemno !== itemId) {
							replacedItems.push({ id: itemId, type: 'ART' });
						}
						productItems.push({ id: itemId, type: 'ART' });
						exists.push(itemId);
					}
				});
			}
		});

		return { productItems, replacedItems, groupedYmalProducts };
	}

	// TODO: Remove localReplacements logic when CA dexf data is correct
	private getLocalReplacement(
		localSofabedMattressId: string | null,
		item: SprItem,
		globalSofabedMattressId: string[],
		countryCode: string
	) {
		return localSofabedMattressId && globalSofabedMattressId.includes(item.itemno)
			? localSofabedMattressId
			: localReplacements[countryCode] &&
			  localReplacements[countryCode]['globalToLocal'][item.itemno]
			? localReplacements[countryCode]['globalToLocal'][item.itemno]
			: item.itemno;
	}
}
